import type { Word } from '@attain-apps/string-builder'

import * as common from '../common'
import * as dms from '../dms'
import * as mosaic from '../mosaic'

export interface Field<TType extends dms.data.Value.Type = dms.data.Value.Type>
  extends common.Identified.Optionally<Field.Id> {
  // NOTE: everything is optional so that we are forced to handle it
  value?: dms.data.Value<TType>
  /**
   * @see {@link Field.Metadata.Source}
   */
  meta?: Field.Metadata.Source<TType>
  /**
   * In some cases, the {@link Field} corresponds to a {@link dms.data.Record}.
   * In those cases, this identifies that {@link dms.data.Record}
   */
  record?: dms.data.Value.Reference.Default
  /**
   * Indicates the {@link Field} should not be editable
   */
  readonly?: boolean
}
export namespace Field {
  /**
   * Optional identifier of a {@link Field}
   *
   * Must be a {@link Word}
   */
  export type Id = Word

  /**
   * Simply a group of {@link Field}
   */
  export interface Group {
    /**
     * Usually in the UI, {@link Field fields} are displayed in columns. This
     * determines how many columns are used for this {@link Group}
     *
     * @default 1, if majority are input fields. 2 otherwise
     */
    columnCount?: number
    // NOTE: everything is optional so that we are forced to handle it
    fields?: readonly Field[]
  }

  /**
   * A collection of {@link Field}, generally to be displayed together in a
   * single widget or modal, potentially in sections
   *
   * @see {@link Collection.WithSections}, {@link Collection.WithoutSections}
   */
  export type Collection = Partial<
    FlattenUnion<Collection.WithSections | Collection.WithoutSections>
  > &
    OnSave
  export namespace Collection {
    /**
     * Multiple {@link Group} of {@link Field}, each being its own
     * {@link Section}
     */
    export interface WithSections {
      // NOTE: everything is optional so that we are forced to handle it
      sections?: readonly Collection.Section[]
    }
    /**
     * A {@link Group} of {@link Field} within a {@link Collection} that
     * contains multiple {@link Field} (i.e. {@link Collection.WithSections})
     */
    export interface Section extends common.Named.Optionally, Group {}

    /**
     * Just a single {@link Group} of {@link Field}
     */
    export type WithoutSections = Group
  }

  export interface OnSave {
    /**
     * Indicates what {@link mosaic.Action} should be executed when the save
     * button is clicked for this {@link Collection}
     *
     * #### Special Values
     * - `${field:<fieldId>}` (e.g. `${field:name}`) - Current
     *   {@link Field.value} for the specified {@link Field}. If the
     *   {@link Field} is not {@link Field.readonly}, this will be the latest
     *   user input for the field
     */
    onSave?: mosaic.Action.Ref
  }
  export namespace OnSave {
    export namespace UserInputVariableValueName {
      /** TODO:  */
      export const ALL_FIELDS = 'fields'
      // eslint-disable-next-line @typescript-eslint/no-shadow
      export type Field<TId extends string> = `${typeof Field.PREFIX}${TId}`
      export namespace Field {
        export const PREFIX = 'field:'

        /**
         * TODO: add js doc
         *
         * @example
         * { 'name': 'John Doe' } => { 'field:name': 'John Doe' }
         */
        export function lookupFrom<TId extends string>(
          lookup: Record<string, unknown>,
        ): Record<string, unknown> {
          const replacedLookup: Partial<Record<string, unknown>> = {}
          for (const alias in lookup) {
            replacedLookup[
              mosaic.Action.Ref.UserInputVariableValue.from(
                `${Field.PREFIX}${alias}`,
              )
            ] = lookup[alias]
          }

          return replacedLookup
        }
      }
    }
  }

  /**
   * Information about how to display a {@link Field}
   */
  // NOTE: everything is optional so that we are forced to handle it
  export interface Metadata<
    TType extends dms.data.Value.Type = dms.data.Value.Type,
  > extends common.Named.Optionally {
    type?: TType
  }
  export namespace Metadata {
    /**
     * Information that can be used to retrieve {@link Field.Metadata}
     */
    // NOTE: everything is optional so that we are forced to handle it
    export type Source<
      TType extends dms.data.Value.Type = dms.data.Value.Type,
    > = Partial<{
      /** @see {@link Source.DmsAttribute} */
      dmsAttr: Source.DmsAttribute
      /** @see {@link Source.DmsRef} */
      dmsRef: Source.DmsRef
      /**
       * Just a direct {@link Metadata} object, no retrieval needed
       */
      direct: Metadata.Source.AsJson
    }>
    export namespace Source {
      /**
       * Retrieves {@link Field.Metadata} from {@link Metadata.Source} and a
       * {@link dms.models.Schema}
       */
      export function getMetadata<TType extends dms.data.Value.Type>(
        { direct, dmsAttr, dmsRef }: Metadata.Source<TType>,
        schema: dms.models.Schema,
      ): Metadata<TType> | undefined {
        if (direct) {
          const metadata = AsJson.getMetadata(direct)

          // NOTE: We cant be sure what the Type will be, but we are just gonna
          // hope that it is what the consumer expects
          return metadata satisfies typeof metadata as Metadata<TType>
        } else if (dmsAttr) {
          const metadata = DmsAttribute.getMetadata(dmsAttr, schema)

          // NOTE: We cant be sure what the Type will be, but we are just gonna
          // hope that it is what the consumer expects
          return metadata satisfies typeof metadata as Metadata<TType>
        } else if (dmsRef) {
          const metadata = DmsRef.getMetadata(dmsRef, schema)

          // NOTE: In this case we know the Type, but we are just gonna hope
          // that the consumer specified the right type for TType
          return metadata satisfies typeof metadata as Metadata<TType>
        }

        // Return undefined since none of the valid metadata sources were
        // provided, let the caller handle it.
        return undefined
      }
      /**
       * Uses a {@link dms.models.Attribute} to get {@link Metadata}
       */
      export interface DmsAttribute {
        // NOTE: everything is optional so that we are forced to handle it
        attributeId?: dms.models.Attribute.Id
      }
      export namespace DmsAttribute {
        /**
         * Retrieves {@link Field.Metadata} using
         * {@link Field.Metadata.Source.DmsAttribute} and a {@link dms.models.Schema}
         */
        // eslint-disable-next-line @typescript-eslint/no-shadow
        export function getMetadata(
          { attributeId }: DmsAttribute,
          schema: dms.models.Schema,
        ): Metadata | undefined {
          if (!attributeId) {
            return undefined
          }
          const attribute = schema.tryGetAttribute(attributeId)
          if (!attribute) {
            // NOTE: Since the schema (models) aren't immediately available, we
            // fall back to using the attribute ID as a last resort, to ensure
            // better UI
            // TODO: This should probably be handled in some other way in the
            // future
            return {
              name: attributeId,
            }
          }
          return {
            name: attribute.name,
            type: attribute.type,
          }
        }
      }

      // TODO: since we are
      /**
       * Sometimes, we want to represent multiple {@link dms.data.Record} as a
       * single {@link Field}, where those {@link dms.data.Record} are related
       * to the {@link Field.recordId} in a many-to-one or many-to-many
       * relationship. In those cases, we can use this to get the appropriate
       * {@link Metadata}.
       *
       * Many-to-one and many-to-many relationships require slightly different
       * information. First, consider the _desired dimension_ or _desired
       * entity_ to be the {@link dms.models.Dimension} or
       * {@link dms.models.Entity} corresponding to the multiple
       * {@link dms.data.Record} that are represented by this {@link Field} and
       * the _base entity_ to be the {@link dms.models.Entity} corresponding to
       * the {@link Field.record}.
       *
       * For both many-to-one and many-to-many relationships,
       * {@link DmsRef.source} should correspond to an
       * {@link dms.models.Attribute} that
       * {@link dms.data.Value.Type.Reference references} the dimension of the
       * _base entity_. For many-to-one relationships, that
       * {@link dms.models.Attribute} should be on the _desired dimension_ or
       * _desired entity_.
       *
       * For many-to-many relationships, we also need {@link DmsRef.target}.
       * This will introduce a third entity, we can refer to as the _passthrough
       * entity_, which can be any {@link dms.models.Entity} that allows for the
       * following: {@link DmsRef.source} should correspond to an
       * {@link dms.models.Attribute} on the _passthrough entity_ that still
       * {@link dms.data.Value.Type.Reference references} the dimension of the
       * _base entity_ and {@link DmsRef.target} should correspond to an
       * {@link dms.models.Attribute} also on the _passthrough entity_ that
       * {@link dms.data.Value.Type.Reference references} the _desired
       * dimension_.
       */
      export interface DmsRef {
        // NOTE: everything is optional so that we are forced to handle it
        /** @see {@link DmsRef} */
        target?: dms.models.Attribute.Id
        /** @see {@link DmsRef} */
        source?: dms.models.Attribute.Id
      }
      export namespace DmsRef {
        /**
         * Retrieves {@link Field.Metadata} using
         * {@link Field.Metadata.Source.DmsRef} and a {@link dms.models.Schema}
         */
        // eslint-disable-next-line @typescript-eslint/no-shadow
        export function getMetadata(
          { source, target }: DmsRef,
          schema: dms.models.Schema,
        ): Metadata<dms.data.Value.Type.Structure.Inline> | undefined {
          // Without a source attribute, we cannot establish either a
          // one-to-many or many-to-many relationship
          if (!source) {
            return
          }

          // If only source is provided, it's a many-to-one relationship
          // If both source and target are provided, it's a many-to-many relationship
          const attr = target
            ? schema.tryGetAttribute(target)
            : schema.tryGetAttribute(source)

          // If the attribute type is not a reference, we can't do anything
          if (!attr || !dms.data.Value.Type.Reference.isReference(attr.type)) {
            return
          }

          // In both many-to-many and many-to-one, we want to use the dimension name as the attribute name
          const dimension = schema.tryGetDimension(attr.type.dimensionId)
          return {
            name: dimension?.name,
            type: {
              inlineStructure: common.value.types.Structure.list(
                common.value.types.Intrinsic.Text,
              ),
            },
          }
        }
      }
      /**
       * Defines metadata for complex {@link Field} types beyond simple
       * {@link dms.data.Value.Type.Intrinsic} types. Since queries can only
       * accept pure JSON, {@link AsJson} provides a way to represent complex
       * types like enums and structures in field metadata.
       *
       * @see {@link dms.data.Value.Type.Enum},
       * {@link dms.data.Value.Type.Structure}
       */
      export interface AsJson extends Pick<Metadata, 'name'> {
        /**
         * Used to determine the {@link dms.data.Value.Type} of the {@link Field}
         */
        // NOTE: everything is optional so that we are forced to handle it
        type?:
          | common.value.types.Intrinsic
          | ExactlyOneOf<
              UnionToIntersection<
                Exclude<
                  dms.models.Attribute.Type,
                  // NOTE: We intentionally exclude 'dms.data.valueType.Intrinsic' in favor of
                  // 'common.value.types.Intrinsic' to simplify type definitions.
                  // This allows using plain values like `type: 'TEXT'` instead of verbose objects like
                  // `type: { intrinsic: 'TEXT' }`.
                  common.value.types.Intrinsic
                >
              > & {
                /**
                 * Configuration for creating an {@link dms.data.Value.Type.Enum.Inline} type
                 *
                 * @see {@link common.value.types.Enum.constructor.Params}
                 */
                inlineEnum: common.value.types.Enum.constructor.Params
                // TODO: Finish implementing structure type
                inlineStructure: unknown
              }
            >
      }
      export namespace AsJson {
        /**
         * Gets {@link Field.Metadata} by analyzing {@link AsJson} and determining
         * which {@link dms.data.Value.Type} to use based on the supplied
         * {@link AsJson.type}
         *
         * __NOTE__: Even though TypeScript uses `ExactlyOneOf` to prevent multiple type configurations in {@link AsJson.type},
         * some cases might allow multiple configurations. In such cases, the precedence order is:
         * 1. intrinsic
         * 2. inlineEnum
         * 3. {@link dms.data.Value.Type.Enum}
         * 4. {@link dms.data.Value.Type.Structure}
         */
        // eslint-disable-next-line @typescript-eslint/no-shadow
        export function getMetadata({
          type,
          name,
        }: AsJson): Metadata | undefined {
          /**
           * For {@link common.value.types.Intrinsic} types, convert to
           * {@link dms.data.Value.Type.Intrinsic} format and return.
           */
          if (common.value.types.Intrinsic.isIntrinsic(type)) {
            return {
              name,
              type,
            }
          }

          /**
           * Produce {@link dms.data.Value.Type.Enum} type from the
           * {@link common.value.types.Enum.constructor.Params}.
           */
          if (type?.inlineEnum) {
            return {
              name,
              // NOTE: this should ideally be
              // dms.data.Value.Type.Enum.Inline(type.enum), but TypeScript
              // namespace type doesn't include constructors
              type: {
                inlineEnum: new common.value.types.Enum(type.inlineEnum),
              },
            }
          }

          /**
           * Directly reference an {@link dms.models.Enum} by its
           * {@link dms.models.Enum.Id}
           */
          if (dms.data.Value.Type.Enum.isEnum(type)) {
            return { name, type }
          }

          /**
           * For {@link dms.data.Value.Type.Structure} types, convert to
           * {@link dms.data.Value.Type.Structure.Inline}
           * format and return.
           */
          if (type?.inlineStructure) {
            // TODO: Structure type implementation pending architectural
            // decisions
            throw new Error('Structure is not supported yet')
          }

          // In case it is neither of the above, return undefined
          return
        }
      }
    }
  }
}
